import React, { useEffect } from 'react';
import TableHeader from './TableHeader';
import TableBody from './TableBody';
import TableFooter from './Footer/TableFooter';
import { getTableClass } from '../../../../util/table-helper';
import { Opacity } from '../../../../enum/Opacity';
import ViewPerCurrency from './Footer/ViewPerCurrency';
import ExpandSum from './Footer/ExpandSum';
import { useAppSelector } from '../../../../hook/useAppSelector';
import { useColumnDragDrop } from '../../../../hook/useColumnDragDrop';
import { Column } from '../../../../enum/Column';

interface TableColumnProps {
  column: Column;
  data: Record<string, number>[];
  isFirstColumnAfterCCY: boolean;
  isLastColumn: boolean;
  showFooter: boolean;
  className?: string;
}

const TableColumn: React.FC<TableColumnProps> = ({
  column,
  data,
  isFirstColumnAfterCCY,
  isLastColumn,
  showFooter,
  className,
}) => {
  const shouldViewPerCurrency = useAppSelector((state) => state.data.shouldViewPerCurrency);
  const shouldExpandSum = useAppSelector((state) => state.data.shouldExpandSum);

  const { ref, isDragging } = useColumnDragDrop({
    column,
  });

  useEffect(() => {
    const updateStickyOffsets = () => {
      const columns = document.querySelectorAll('.table_column.sticky_column');
      let offset = 0;

      columns.forEach((col) => {
        (col as HTMLElement).style.left = `${offset}px`;
        offset += (col as HTMLElement).offsetWidth;
      });
    };

    updateStickyOffsets();
    window.addEventListener('resize', updateStickyOffsets);

    return () => {
      window.removeEventListener('resize', updateStickyOffsets);
    };
  }, []);

  const setFooter = () => {
    return (
      <>
        <ViewPerCurrency
          column={column.name}
          isLastColumn={isLastColumn}
          className={shouldViewPerCurrency && !shouldExpandSum ? 'visible_footer' : 'hidden_footer'}
        ></ViewPerCurrency>

        <ExpandSum
          column={column.name}
          isFirstColumnAfterCCY={isFirstColumnAfterCCY}
          isLastColumn={isLastColumn}
          className={shouldExpandSum && !shouldViewPerCurrency ? 'visible_footer' : 'hidden_footer'}
        ></ExpandSum>

        {!shouldViewPerCurrency && !shouldExpandSum && (
          <TableFooter
            column={column.name}
            isFirstColumnAfterCCY={isFirstColumnAfterCCY}
            isLastColumn={isLastColumn}
          ></TableFooter>
        )}
      </>
    );
  };

  return (
    <div
      ref={column.isLocked ? null : ref}
      style={{
        opacity: isDragging ? Opacity.DRAGGING : Opacity.DEFAULT,
      }}
      className={`table_column ${getTableClass(column.name, 'column')} ${`${className}_column`}`}
    >
      <TableHeader column={column.name} />
      <TableBody column={column.name} data={data} />
      {showFooter && setFooter()}
    </div>
  );
};

export default TableColumn;
